import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle,
  Clock,
  Download,
  Edit3,
  Power,
  Radio,
  RefreshCw,
  Save,
  Search,
  Send,
  ShieldOff,
  ShieldCheck,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import { commandService, vehicleService } from '../services/api';
import { exportCsv } from '../utils/csv';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const emptyForm = {
  vehicle_id: '',
  type: 'request_position',
  status: 'pending',
  command: '',
  notes: '',
};

const commandLabels = {
  block: 'Bloquear veiculo',
  unblock: 'Desbloquear veiculo',
  request_position: 'Solicitar posicao',
  reboot: 'Reiniciar rastreador',
  custom: 'Personalizado',
};

const commandIcons = {
  block: ShieldOff,
  unblock: ShieldCheck,
  request_position: Radio,
  reboot: Power,
  custom: Send,
};

const statusLabels = {
  pending: 'Pendente',
  sent: 'Enviado',
  executed: 'Executado',
  failed: 'Falhou',
  cancelled: 'Cancelado',
};

const statusClass = {
  pending: 'badge-warning',
  sent: 'badge-info',
  executed: 'badge-success',
  failed: 'badge-danger',
  cancelled: 'badge-info',
};

const statusOptions = ['pending', 'sent', 'executed', 'failed', 'cancelled'];

const dateLabel = (value) => {
  if (!value) return '-';
  return format(new Date(value), 'dd/MM/yyyy HH:mm');
};

const Commands = () => {
  const [vehicles, setVehicles] = useState([]);
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const [vehicleResponse, commandResponse] = await Promise.all([
        vehicleService.getAll(),
        commandService.getAll(),
      ]);
      setVehicles(vehicleResponse.data.data || []);
      setCommands(commandResponse.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao carregar comandos');
    } finally {
      setLoading(false);
    }
  };

  const filteredCommands = useMemo(() => {
    const term = search.trim().toLowerCase();

    return commands.filter((command) => {
      const matchesStatus = statusFilter === 'all' || command.status === statusFilter;
      const matchesType = typeFilter === 'all' || command.type === typeFilter;
      const matchesSearch = !term || [
        command.command,
        command.notes,
        command.response,
        command.vehicle?.name,
        command.vehicle?.plate,
        command.vehicle?.imei,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));

      return matchesStatus && matchesType && matchesSearch;
    });
  }, [commands, search, statusFilter, typeFilter]);

  const pendingCount = commands.filter((command) => command.status === 'pending').length;
  const sentCount = commands.filter((command) => command.status === 'sent').length;
  const executedCount = commands.filter((command) => command.status === 'executed').length;
  const failedCount = commands.filter((command) => command.status === 'failed').length;

  const openCreateModal = (type = 'request_position') => {
    setFormData({
      ...emptyForm,
      type,
      vehicle_id: vehicles[0]?.id || '',
    });
    setModalOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const response = await commandService.create(formData);
      setCommands((current) => [response.data.data, ...current]);
      setModalOpen(false);
      setFormData(emptyForm);
      toast.success('Comando registrado com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar comando');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (command, status) => {
    try {
      const response = await commandService.updateStatus(command.id, {
        status,
        response: status === 'executed' ? 'Execucao confirmada manualmente' : command.response,
      });
      setCommands((current) => current.map((item) => (
        item.id === command.id ? response.data.data : item
      )));
      toast.success('Status atualizado');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar status');
    }
  };

  const handleDelete = async (command) => {
    const confirmed = window.confirm(`Remover o comando ${command.command}?`);
    if (!confirmed) return;

    try {
      await commandService.delete(command.id);
      setCommands((current) => current.filter((item) => item.id !== command.id));
      toast.success('Comando removido');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao remover comando');
    }
  };

  const handleExport = () => {
    exportCsv('rcrastro-comandos.csv', [
      { key: 'created_at', label: 'Criado em' },
      { key: 'vehicle', label: 'Veiculo' },
      { key: 'plate', label: 'Placa' },
      { key: 'type', label: 'Tipo' },
      { key: 'command', label: 'Comando' },
      { key: 'status', label: 'Status' },
      { key: 'sent_at', label: 'Enviado em' },
      { key: 'executed_at', label: 'Executado em' },
      { key: 'response', label: 'Resposta' },
    ], filteredCommands.map((command) => ({
      created_at: dateLabel(command.created_at),
      vehicle: command.vehicle?.name || '',
      plate: command.vehicle?.plate || '',
      type: commandLabels[command.type] || command.type,
      command: command.command,
      status: statusLabels[command.status] || command.status,
      sent_at: dateLabel(command.sent_at),
      executed_at: dateLabel(command.executed_at),
      response: command.response || '',
    })));
    toast.success('CSV de comandos gerado');
  };

  const QuickButton = ({ type }) => {
    const Icon = commandIcons[type] || Send;

    return (
      <button
        type="button"
        onClick={() => openCreateModal(type)}
        className="rounded-lg border border-dark-700 bg-dark-800 p-4 text-left transition-colors hover:border-primary-500 hover:bg-dark-700"
      >
        <Icon className="mb-3 h-6 w-6 text-primary-400" />
        <div className="font-semibold text-white">{commandLabels[type]}</div>
      </button>
    );
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Comandos remotos</h1>
              <p className="text-dark-400">Registre e acompanhe comandos enviados aos rastreadores.</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleExport}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                Exportar CSV
              </button>
              <button
                type="button"
                onClick={loadData}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Atualizar
              </button>
              <button
                type="button"
                onClick={() => openCreateModal()}
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Send className="h-4 w-4" />
                Novo comando
              </button>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Clock className="h-4 w-4" />
                Pendentes
              </div>
              <p className="text-3xl font-bold text-warning-400">{pendingCount}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <Send className="h-4 w-4" />
                Enviados
              </div>
              <p className="text-3xl font-bold text-primary-400">{sentCount}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <CheckCircle className="h-4 w-4" />
                Executados
              </div>
              <p className="text-3xl font-bold text-success-400">{executedCount}</p>
            </div>
            <div className="card">
              <div className="mb-2 flex items-center gap-2 text-dark-400">
                <XCircle className="h-4 w-4" />
                Falhas
              </div>
              <p className="text-3xl font-bold text-danger-400">{failedCount}</p>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-5">
            <QuickButton type="request_position" />
            <QuickButton type="block" />
            <QuickButton type="unblock" />
            <QuickButton type="reboot" />
            <QuickButton type="custom" />
          </div>

          <div className="card mb-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_220px_220px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-dark-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="input-field pl-11"
                  placeholder="Buscar por veiculo, placa, IMEI, comando ou resposta"
                />
              </div>
              <select
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                className="input-field"
              >
                <option value="all">Todos os tipos</option>
                {Object.entries(commandLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="input-field"
              >
                <option value="all">Todos os status</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] text-left">
                <thead className="border-b border-dark-700 text-sm text-dark-400">
                  <tr>
                    <th className="px-5 py-4 font-medium">Comando</th>
                    <th className="px-5 py-4 font-medium">Veiculo</th>
                    <th className="px-5 py-4 font-medium">Datas</th>
                    <th className="px-5 py-4 font-medium">Status</th>
                    <th className="px-5 py-4 font-medium">Resposta</th>
                    <th className="px-5 py-4 text-right font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        Carregando comandos...
                      </td>
                    </tr>
                  ) : filteredCommands.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-10 text-center text-dark-400">
                        <Send className="mx-auto mb-2 h-10 w-10 opacity-50" />
                        Nenhum comando encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredCommands.map((command) => {
                      const Icon = commandIcons[command.type] || Send;

                      return (
                        <tr key={command.id} className="border-b border-dark-800 text-sm last:border-0">
                          <td className="px-5 py-4">
                            <div className="flex items-start gap-3">
                              <div className="mt-1 rounded-lg bg-dark-700 p-2 text-primary-400">
                                <Icon className="h-4 w-4" />
                              </div>
                              <div>
                                <div className="font-semibold text-white">{commandLabels[command.type] || command.type}</div>
                                <div className="text-dark-400">{command.command}</div>
                                <div className="text-xs text-dark-500">{command.notes || 'Sem observacao'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-dark-300">
                            <div className="font-medium text-white">{command.vehicle?.name || 'Veiculo removido'}</div>
                            <div>{command.vehicle?.plate || '-'}</div>
                            <div className="text-xs text-dark-500">{command.vehicle?.imei || 'Sem IMEI'}</div>
                          </td>
                          <td className="px-5 py-4 text-dark-300">
                            <div>Criado {dateLabel(command.created_at)}</div>
                            <div className="text-xs text-dark-500">Enviado {dateLabel(command.sent_at)}</div>
                            <div className="text-xs text-dark-500">Executado {dateLabel(command.executed_at)}</div>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`badge ${statusClass[command.status] || 'badge-info'}`}>
                              {statusLabels[command.status] || command.status}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-dark-300">
                            {command.response || '-'}
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex justify-end gap-2">
                              {command.status === 'pending' && (
                                <button
                                  type="button"
                                  onClick={() => updateStatus(command, 'sent')}
                                  className="rounded-lg p-2 text-primary-400 transition-colors hover:bg-dark-700"
                                  title="Marcar enviado"
                                >
                                  <Send className="h-4 w-4" />
                                </button>
                              )}
                              {command.status !== 'executed' && command.status !== 'cancelled' && (
                                <button
                                  type="button"
                                  onClick={() => updateStatus(command, 'executed')}
                                  className="rounded-lg p-2 text-success-400 transition-colors hover:bg-dark-700"
                                  title="Marcar executado"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              )}
                              {command.status !== 'failed' && command.status !== 'executed' && (
                                <button
                                  type="button"
                                  onClick={() => updateStatus(command, 'failed')}
                                  className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700"
                                  title="Marcar falha"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDelete(command)}
                                className="rounded-lg p-2 text-danger-400 transition-colors hover:bg-dark-700"
                                title="Remover"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-dark-700 bg-dark-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-800 p-5">
              <div>
                <h2 className="text-lg font-bold text-white">Novo comando</h2>
                <p className="text-sm text-dark-400">Selecione o veiculo e o comando remoto.</p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-dark-800 hover:text-white"
                title="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Veiculo</label>
                  <select
                    name="vehicle_id"
                    value={formData.vehicle_id}
                    onChange={handleChange}
                    className="input-field"
                    required
                  >
                    <option value="">Selecionar veiculo</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.name} - {vehicle.plate}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Tipo</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="input-field"
                  >
                    {Object.entries(commandLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Status inicial</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="input-field"
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>{statusLabels[status]}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-dark-300">Comando personalizado</label>
                  <input
                    name="command"
                    value={formData.command}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Somente para tipo personalizado"
                    disabled={formData.type !== 'custom'}
                    required={formData.type === 'custom'}
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-dark-300">Observacoes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="input-field min-h-24"
                  placeholder="Motivo, operador, protocolo ou instrucao interna"
                />
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-dark-800 pt-5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Salvando...' : 'Registrar comando'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Commands;
